import * as THREE from "three";
import { GlobalState, type PlayerState, type PublicGameSnapshot } from "@ict/shared";
import { getSocket, EVENTS } from "@/network/SocketClient";
import { ClockSync } from "@/network/ClockSync";
import { GameEngine } from "@/game/GameEngine";
import { Arena } from "@/world/Arena";
import { PlayerController } from "@/player/PlayerController";
import { RemotePlayerManager } from "@/player/RemotePlayerManager";
import { NetworkPlayerSync } from "@/player/NetworkPlayerSync";
import { TargetsController } from "@/targets/TargetsController";
import { CombatController } from "@/combat/CombatController";
import { CeremonyController, type CeremonyHost } from "@/ceremony/CeremonyController";
import type { VideoReadyInfo } from "@/video/VideoPreloader";
import { AudioManager } from "@/audio/AudioManager";
import { ScreenManager } from "@/ui/screens";
import { HUD, type TargetMarkerInfo } from "@/ui/hud";
import { DebugPanel } from "@/ui/debugPanel";
import { TouchControls } from "@/ui/touchControls";
import { t } from "@/i18n/i18n";

const SESSION_TOKEN_KEY = "ict_session_token";
const SESSION_NAME_KEY = "ict_session_name";
const SESSION_CODE_KEY = "ict_session_code";

const app = document.getElementById("app")!;

const canvas = document.createElement("canvas");
canvas.id = "game-canvas";
app.appendChild(canvas);

const screens = new ScreenManager(app);
const hud = new HUD(app);
hud.setVisible(false);
const debugPanel = new DebugPanel(app);
debugPanel.setVisible(false); // hidden by default; toggle with F3 or ` (see CEREMONY_SYNC.md)

const socket = getSocket();
const clockSync = new ClockSync(socket);
const audio = new AudioManager();

let myPlayerId: string | null = null;
let requiredPlayers = 4;
let roster = new Map<string, PlayerState>();
let currentGlobalState: GlobalState = GlobalState.LOBBY;
let latestVideoInfo: VideoReadyInfo | null = null;

// Persisted to sessionStorage (per-tab — see README on why not localStorage)
// so that a dropped connection, a server restart, *or* the browser/OS
// discarding and reloading the page entirely (iOS Safari does this
// aggressively for memory-heavy tabs like a WebGL game when backgrounded)
// can all recover without the player retyping the access code / name.
let cachedAccessCode: string | null = sessionStorage.getItem(SESSION_CODE_KEY);
let cachedName: string | null = sessionStorage.getItem(SESSION_NAME_KEY);
let hasJoinedOnce = false;

// True only at page-load time: a prior session in this tab got far enough
// to have a token, name, and code saved, so we can skip straight past the
// access-code/name screens after the one required tap (see ENTER MISSION
// handler below) instead of making the player type everything again.
const hasSavedSession = !!(sessionStorage.getItem(SESSION_TOKEN_KEY) && cachedName && cachedAccessCode);

let engine: GameEngine | null = null;
let arena: Arena | null = null;
let player: PlayerController | null = null;
let remotes: RemotePlayerManager | null = null;
let netSync: NetworkPlayerSync | null = null;
let targets: TargetsController | null = null;
let combat: CombatController | null = null;
let touch: TouchControls | null = null;
let ceremony: CeremonyController | null = null;

let fps = 0;

function rosterArray(): PlayerState[] {
  return [...roster.values()].filter((p) => p.connected);
}

function refreshLobbyUI(): void {
  if (currentGlobalState === GlobalState.LOBBY) {
    screens.updateLobby(rosterArray(), requiredPlayers);
  }
}

function refreshHudRoster(): void {
  hud.setPlayerStatuses(rosterArray());
}

const ceremonyHost: CeremonyHost = {
  suspendGameEngine() {
    engine?.stop();
    engine?.disposeSceneContents();
    hud.setVisible(false);
    touch?.setVisible(false);
  },
};

function isTouchDevice(): boolean {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

function initGameWorld(snapshot: PublicGameSnapshot): void {
  engine = new GameEngine(canvas);
  arena = new Arena(engine.scene, engine.quality);
  arena.build();

  const angle = Math.random() * Math.PI * 2;
  const spawn = { x: Math.cos(angle) * 3, y: 0, z: Math.sin(angle) * 3 };

  player = new PlayerController(engine.scene, engine.camera, canvas, spawn);
  remotes = new RemotePlayerManager(engine.scene);
  netSync = new NetworkPlayerSync(socket, player, remotes, myPlayerId!);
  netSync.hydrateFromSnapshot(snapshot.players);

  targets = new TargetsController(engine.scene, myPlayerId!);
  if (snapshot.targets.length > 0) targets.setTargets(snapshot.targets);

  combat = new CombatController(socket, engine.camera, canvas, player, targets, (reason) => hud.showReject(reason));
  combat.attachServerListeners();

  const me = roster.get(myPlayerId!);
  if (me?.missionComplete) {
    hud.showMissionComplete();
    combat.setEnabled(false);
  }

  if (isTouchDevice()) {
    touch = new TouchControls(app, {
      onMove: (x, y) => player?.setTouchInput({ moveX: x, moveY: y }),
      onRun: (r) => player?.setTouchInput({ run: r }),
      onJump: () => player?.setTouchInput({ jumpPressed: true }),
      onAttack: () => combat?.manualAttack(),
    });
  }

  engine.onUpdate((dt, elapsed) => {
    player!.update(dt);
    remotes!.update(dt);
    netSync!.update(dt);
    targets!.update(dt, elapsed);
    arena!.update(dt, elapsed);
    hud.updateTargetMarker(computeTargetMarker());

    fps = fps * 0.9 + (dt > 0 ? 1 / dt : fps) * 0.1;
    debugPanel.update({
      fps,
      ping: me?.ping ?? 0,
      clockOffsetMs: clockSync.getOffset(),
      position: player!.getPosition(),
      targetId: targets!.getOwnTargetId(),
      targetOwner: myPlayerId,
      globalState: currentGlobalState,
      videoReady: latestVideoInfo?.ready ?? false,
      videoBufferedSeconds: latestVideoInfo?.bufferedSeconds ?? 0,
      videoSyncErrorMs: ceremony?.getDebugInfo().syncErrorMs ?? null,
    });
  });
}

function computeTargetMarker(): TargetMarkerInfo | null {
  if (!targets || !player || !engine) return null;
  const ownId = targets.getOwnTargetId();
  if (!ownId) return null;
  const pos = targets.getPosition(ownId);
  if (!pos) return null;

  const projected = new THREE.Vector3(pos.x, pos.y, pos.z).project(engine.camera);
  const sx = (projected.x * 0.5 + 0.5) * window.innerWidth;
  const sy = (-projected.y * 0.5 + 0.5) * window.innerHeight;
  const onScreen = projected.z < 1 && sx >= 0 && sx <= window.innerWidth && sy >= 0 && sy <= window.innerHeight;
  const dist = targets.distanceFromPlayer(ownId, player.getPosition()) ?? 0;

  return { screenX: sx, screenY: sy, visible: onScreen, distanceMeters: dist, label: t("hud.target") };
}

function applyGlobalState(state: GlobalState): void {
  currentGlobalState = state;
  switch (state) {
    case GlobalState.LOBBY:
      engine?.stop();
      screens.showLobby();
      refreshLobbyUI();
      hud.setVisible(false);
      touch?.setVisible(false);
      break;
    case GlobalState.MISSION_RUNNING:
      screens.hideAllScreens();
      hud.setVisible(true);
      hud.hideMissionComplete();
      player?.setInputEnabled(true);
      combat?.setEnabled(true);
      engine?.start();
      audio.startAmbient();
      touch?.setVisible(true);
      hud.showBanner(t("hud.missionStart"));
      window.setTimeout(() => hud.showBanner(t("hud.findYourTarget")), 1600);
      break;
    case GlobalState.MISSION_COMPLETE:
      player?.setInputEnabled(false);
      combat?.setEnabled(false);
      hud.showBanner(t("hud.allTargetsCleared"));
      break;
    case GlobalState.CEREMONY_PREPARING:
    case GlobalState.CEREMONY_COUNTDOWN:
    case GlobalState.CEREMONY_PLAYING:
      hud.setVisible(false);
      touch?.setVisible(false);
      break;
    case GlobalState.CEREMONY_FINISHED:
      break;
  }
}

// Pause rendering while the tab is backgrounded — saves battery/CPU/GPU and
// reduces the chance of iOS Safari deciding a memory-heavy WebGL tab should
// be discarded and reloaded from scratch while the player has switched apps.
document.addEventListener("visibilitychange", () => {
  if (!engine) return;
  if (document.hidden) {
    engine.stop();
  } else if (currentGlobalState === GlobalState.MISSION_RUNNING) {
    engine.start();
  }
});

function submitName(name: string): void {
  cachedName = name;
  sessionStorage.setItem(SESSION_NAME_KEY, name);
  ensureFullscreen(); // second chance at fullscreen — see ensureFullscreen() below
  const sessionToken = sessionStorage.getItem(SESSION_TOKEN_KEY) ?? undefined;
  socket.emit(EVENTS.PLAYER_JOIN, { name, sessionToken });
  screens.showLoading();
  screens.setLoadingProgress(10, t("loading.connecting"));
}

// ---- Landing / access / name flow ----

/**
 * Best-effort fullscreen — stage/kiosk displays should be immersive.
 * Deliberately paranoid: Safari/iPadOS's prefixed `webkitRequestFullscreen`
 * is not promise-based and can throw *synchronously* (unlike the modern
 * `requestFullscreen`, which only ever rejects). An uncaught throw here
 * would abort the entire async ENTER MISSION handler below it, silently
 * stranding the player on a screen that looks stuck — never let this step
 * take anything else down with it.
 */
function requestFullscreen(): void {
  try {
    const el = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => void;
    };
    const request = el.requestFullscreen?.bind(el) ?? el.webkitRequestFullscreen?.bind(el);
    if (!request) return;
    const result = request() as unknown;
    if (result && typeof (result as Promise<void>).catch === "function") {
      (result as Promise<void>).catch(() => undefined);
    }
  } catch {
    // Unsupported/disallowed in this context (e.g. embedded webview) — fine, just skip it.
  }
}

/** If a prior tap didn't actually land us in fullscreen (denied, dismissed, timing), try again. */
function ensureFullscreen(): void {
  const doc = document as Document & { webkitFullscreenElement?: Element | null };
  const inFullscreen = !!(document.fullscreenElement || doc.webkitFullscreenElement);
  if (!inFullscreen) requestFullscreen();
}

function showAccessCodeScreen(): void {
  screens.showAccessCode((code) => {
    cachedAccessCode = code;
    sessionStorage.setItem(SESSION_CODE_KEY, code);
    socket.emit(EVENTS.ACCESS_CODE_SUBMIT, { code });
  });
}

screens.showLanding(async () => {
  // Every step below touches a browser API with known mobile Safari quirks
  // (fullscreen, AudioContext, video autoplay priming). Each is wrapped so
  // one device-specific failure can never prevent reaching the access-code
  // screen — a stuck ENTER MISSION button is worse than a missing fullscreen.
  requestFullscreen();

  try {
    await audio.resumeContext();
  } catch (err) {
    console.warn("[main] audio.resumeContext() failed, continuing without it", err);
  }

  if (!ceremony) {
    try {
      ceremony = new CeremonyController(socket, clockSync, ceremonyHost, audio, app);
      ceremony.preloader.onReadyChange((info) => {
        latestVideoInfo = info;
        if (myPlayerId) {
          socket.emit(EVENTS.VIDEO_READY, { ready: info.ready, bufferedSeconds: info.bufferedSeconds });
        }
      });
      await ceremony.preloader.primeForAutoplay();
    } catch (err) {
      console.warn("[main] ceremony/video priming failed, continuing without it", err);
    }
  }

  if (hasSavedSession && cachedAccessCode) {
    // A saved session survived a reload (e.g. iOS Safari discarding the tab
    // while backgrounded) — skip straight past access-code/name screens.
    screens.showLoading();
    screens.setLoadingProgress(10, t("loading.resuming"));
    socket.emit(EVENTS.ACCESS_CODE_SUBMIT, { code: cachedAccessCode });
  } else {
    showAccessCodeScreen();
  }
});

// If the underlying socket drops and reconnects (flaky network, or the
// server process restarting) after we already joined once, silently replay
// access-code + join instead of leaving the player stuck on a dead screen.
socket.on("connect", () => {
  if (hasJoinedOnce && cachedAccessCode) {
    socket.emit(EVENTS.ACCESS_CODE_SUBMIT, { code: cachedAccessCode });
  }
});

socket.on(EVENTS.ACCESS_RESULT, ({ ok }) => {
  if (hasJoinedOnce) {
    if (ok && cachedName) {
      const sessionToken = sessionStorage.getItem(SESSION_TOKEN_KEY) ?? undefined;
      socket.emit(EVENTS.PLAYER_JOIN, { name: cachedName, sessionToken });
    }
    return;
  }
  if (hasSavedSession && cachedName) {
    if (ok) {
      submitName(cachedName);
    } else {
      // Saved code no longer valid (e.g. admin changed ACCESS_CODE) — drop
      // it and fall back to a normal manual entry instead of looping forever.
      sessionStorage.removeItem(SESSION_CODE_KEY);
      cachedAccessCode = null;
      showAccessCodeScreen();
      screens.showAccessDenied();
    }
    return;
  }
  if (ok) {
    screens.showAccessGranted();
    window.setTimeout(() => screens.showNameEntry(submitName), 450);
  } else {
    screens.showAccessDenied();
  }
});

socket.on(EVENTS.JOIN_ERROR, ({ reason }) => {
  screens.showNameEntry(submitName);
  screens.showNameError(reason);
});

socket.on(EVENTS.JOINED, (payload) => {
  const firstJoin = !hasJoinedOnce;
  hasJoinedOnce = true;
  myPlayerId = payload.playerId;
  sessionStorage.setItem(SESSION_TOKEN_KEY, payload.sessionToken);
  requiredPlayers = payload.snapshot.config.requiredPlayers;
  roster = new Map(payload.snapshot.players.map((p) => [p.playerId, p]));

  if (firstJoin) {
    screens.setLoadingProgress(40, t("loading.buildingWorld"));
    initGameWorld(payload.snapshot);
    screens.setLoadingProgress(80, t("loading.syncingClock"));
  } else {
    // Reconnect: keep the existing engine/world, just resync state.
    targets?.setTargets(payload.snapshot.targets);
    netSync?.hydrateFromSnapshot(payload.snapshot.players);
  }

  clockSync.warmup().then(() => {
    clockSync.startBackgroundSync();
    screens.setLoadingProgress(100, t("loading.ready"));
    applyGlobalState(payload.snapshot.state);
    if (payload.snapshot.ceremony) {
      ceremony?.resumeFromState(payload.snapshot.state, payload.snapshot.ceremony.ceremonyStartAt);
    }
  });

  if (latestVideoInfo) {
    socket.emit(EVENTS.VIDEO_READY, { ready: latestVideoInfo.ready, bufferedSeconds: latestVideoInfo.bufferedSeconds });
  }
});

// ---- Roster / lobby maintenance ----

socket.on(EVENTS.PLAYER_JOINED, ({ player: p }) => {
  roster.set(p.playerId, p);
  refreshLobbyUI();
  refreshHudRoster();
});

socket.on(EVENTS.PLAYER_LEFT, ({ playerId }) => {
  roster.delete(playerId);
  refreshLobbyUI();
  refreshHudRoster();
});

socket.on(EVENTS.PLAYER_READY_CHANGED, ({ playerId, ready }) => {
  const p = roster.get(playerId);
  if (p) {
    p.ready = ready;
    refreshLobbyUI();
  }
});

socket.on(EVENTS.PLAYER_MISSION_COMPLETE, ({ playerId }) => {
  const p = roster.get(playerId);
  if (p) p.missionComplete = true;
  refreshHudRoster();
  if (playerId === myPlayerId) {
    hud.showMissionComplete();
    combat?.setEnabled(false);
    audio.play("missionComplete");
  }
});

socket.on(EVENTS.GLOBAL_PROGRESS, ({ completedCount, totalCount }) => {
  hud.setProgress(completedCount, totalCount);
});

socket.on(EVENTS.MISSION_START, ({ targets: targetList }) => {
  targets?.setTargets(targetList);
  hud.hideMissionComplete();
  combat?.setEnabled(true);
});

socket.on(EVENTS.GLOBAL_STATE_CHANGED, ({ state }) => applyGlobalState(state));

socket.on(EVENTS.TARGET_HIT, () => audio.play("targetHit", 0.8));
socket.on(EVENTS.TARGET_DESTROYED, () => audio.play("targetDestroy"));
socket.on(EVENTS.TARGET_MOVED, ({ targetId, position }) => targets?.applyMove(targetId, position));

// ---- Debug panel toggle ----
window.addEventListener("keydown", (e) => {
  if (e.code === "F3" || e.code === "Backquote") debugPanel.toggle();
});
